import React, { useCallback, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { inject, observer } from "mobx-react";
import { ToastContainer } from "react-toastify";
import { withAuthentication } from "../../components/AuthenticateUser";
import WorkspaceNavBar from "./WorkspaceNavBar";
import WorkspaceHome from "./WorkspaceHome";
import ProgramHome from "./ProgramHome";
import CommandPalette from "./CommandPalette";
import TutorialRunner from "../../components/TutorialRunner";
import { useKeyboardShortcuts } from "./useKeyboardShortcuts";
import { useCurrentWorkspaceIds } from "./useCurrentWorkspaceIds";
import WorkspaceErrorBoundary from "./WorkspaceErrorBoundary";
import ReleaseNotesModal from "./ReleaseNotesModal";
import { ReleaseNotesContext } from "./ReleaseNotesContext";
import { useReleaseNotes } from "./useReleaseNotes";
import { releaseNotesEnabled } from "./releaseNotesFlag";
import SystemBroadcastBanner from "./SystemBroadcastBanner";

// Next-gen-gated. Resolved once at module load (mirrors CHAT_STORE_ENABLED);
// a localStorage override / redeploy flips it. OFF → nav bar + frame render
// exactly as before (no provider, no modal).
const RELEASE_NOTES_ON = releaseNotesEnabled();

export { WorkspaceHome, ProgramHome };

// ProjectView lives in its own module — it imports the live ChatSession
// chain, which we keep out of WorkspaceApp's eagerly-resolved module graph so unit
// tests that load just the layout don't pull in the engine. Callers route
// through ./ProjectView directly.

const WorkspaceAppFrame = ({ userStore }) => {
  const params = useParams();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  const dispatchNewChat = useCallback(() => {
    if (params.program && params.project) {
      // ProjectView listens for workspace:new-chat; if it's mounted, it clears the
      // engine and navigates to a fresh chat URL. If we're not on a project,
      // fall back to navigating to the workspace home.
      window.dispatchEvent(new CustomEvent("workspace:new-chat"));
      navigate(`/v2/p/${params.program}/${params.project}`);
    } else {
      navigate("/v2");
    }
  }, [navigate, params.program, params.project]);

  useKeyboardShortcuts({
    onCommandPalette: openPalette,
    onNewChat: dispatchNewChat,
  });

  // An old link (pre-PLEX-6051 slug ids) is swapped for the current ids.
  useCurrentWorkspaceIds(userStore?.authData, params);

  // Open-state for the "What's New" overlay. Called unconditionally (hooks
  // rule); only surfaced when the feature flag is on. Auto-opens once per
  // version anywhere in v2 on first visit; the account menu re-opens it.
  const releaseNotes = useReleaseNotes(userStore?.authData);

  const frame = (
    <div className="workspace-app" data-testid="v2-app">
      <WorkspaceNavBar
        programId={params.program}
        projectId={params.project}
        chatId={params.chatId}
        onOpenSearch={openPalette}
      />
      <div className="workspace-app__body">
        <WorkspaceErrorBoundary>
          <Outlet />
        </WorkspaceErrorBoundary>
      </div>
      <ToastContainer
        position="bottom-right"
        autoClose={3500}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
        theme="light"
        containerId="v2"
        enableMultiContainer
      />
      <CommandPalette
        open={paletteOpen}
        onClose={closePalette}
        programId={params.program}
        projectId={params.project}
      />
      <SystemBroadcastBanner authData={userStore?.authData} />
      {/* Guided onboarding tour (PLEX-4236). Observes TutorialStore.run;
          renders nothing until a v2 context is started from the welcome
          banner, the account menu, or first project visit. */}
      <TutorialRunner />
      {RELEASE_NOTES_ON && (
        <ReleaseNotesModal
          open={releaseNotes.open}
          onClose={releaseNotes.dismiss}
          notes={releaseNotes.notes}
        />
      )}
    </div>
  );

  if (!RELEASE_NOTES_ON) return frame;
  return (
    <ReleaseNotesContext.Provider value={releaseNotes}>
      {frame}
    </ReleaseNotesContext.Provider>
  );
};

export default withAuthentication(inject("userStore")(observer(WorkspaceAppFrame)));
